export interface ApiResponse<T = any> {
  success: boolean
  message: "OK" | string
  data: T
  [key: string]: any
}

export interface ApiResource<T = any> {
  loading: boolean
  response: ApiResponse<T>
  error: any | null
}

export function createResource<T = any>(
  loading = false,
  response: any | null = null,
  error: any | null = null
): ApiResource<T> {
  return {
    loading,
    response,
    error
  }
}

export function getData(resource: ApiResource<any>): any {
  if (resource.loading || resource.error || !resource.response) {
    return null
  }
  return resource.response.data
}

export function getErrorMessage(resource: ApiResource<any>): string | null {
  // no error, no message
  if (!resource.error) {
    return null
  }

  // try to get error message from response data
  if (
    resource.error.response &&
    resource.error.response.data &&
    resource.error.response.data.message
  ) {
    return resource.error.response.data.message
  }

  // return exception message
  return resource.error.message
}

export async function storeAction(
  action: string,
  commit: CallableFunction,
  callback: CallableFunction
) {
  // set loading, clear error and response
  await commit(action, createResource(true))

  try {
    const response = await callback()

    if (response.success) {
      // success
      await commit(action, createResource(false, response))
    } else {
      // error
      let msg = "Sorry, something went wrong"
      if (response.message) {
        msg += ": " + response.message
      }

      // when AXIOS throws exception, it will add exception.response property so we will do the same here
      await commit(action, createResource(false, null, new ErrorWithResponse(msg, response)))
    }

    // return the response
    return response
  } catch (e) {
    // error from axios (most likely has e.response property with the response)
    await commit(action, createResource(false, null, e))
    return null
  }
}

export class ErrorWithResponse extends Error {
  public response: any | undefined = null
  constructor(message: string | undefined, response: any | undefined) {
    super(message)
    if (response) {
      this.response = response
    }
  }
}

export const urlWithProps = (baseUrl: string, props: { [key: string]: number | string }): string =>
  Object.entries(props).reduce(
    (url: string, [key, value]) => url.replace(`{${key}}`, value.toString()),
    baseUrl
  )

export const formatError = error => {
  const { message: title, data }: { message: string; data: { [field: string]: string[] } } =
    error.response.data
  return {
    title,
    text: data
      ? Object.keys(data)
          .map(key => `${key}: ${Array.isArray(data[key]) ? data[key].join(" - ") : data[key]}`)
          .join(" | ")
      : ""
  }
}

// given the URL and PARAMS it will extend URL with query params
export const createUrl = (url: string, params?: any) => {
  if (!params) {
    params = {}
  }

  let result = url

  const urlParams: string[] = []
  for (const key in params) {
    const value = params[key]
    if (Array.isArray(value)) {
      let arrayIndex = 0
      for (const val of value) {
        if (typeof val !== "object") {
          // params value is an array, we will create url like this: key[]=val1&key[]=val2
          urlParams.push(key + "[]=" + encodeURIComponent(val))
        } else {
          for (const prop in val) {
            if (Object.prototype.hasOwnProperty.call(val, prop)) {
              urlParams.push(
                key + "[" + arrayIndex + "][" + prop + "]=" + encodeURIComponent(val[prop])
              )
            }
          }
        }
        arrayIndex++
      }
    } else if (value !== null && typeof value !== "undefined") {
      // handle properly as axios does, we dont need key=undefined or key=null in the url
      urlParams.push(key + "=" + encodeURIComponent(value))
    }
  }

  // no params need to be added to the url
  if (!urlParams.length) {
    return result
  }

  // add params to the url and return
  if (result.includes("?")) {
    // has question mark
    if (result.substring(result.length - 1) !== "&") {
      result += "&"
    }
  } else {
    // doesn't have question mark
    result += "?"
  }

  return result + urlParams.join("&")
}
